import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ApiCacheService {
  private cache: Map<string, { data: any; timestamp: number }> = new Map();
  
  /**
   * Get cached data for a key if it exists and is not expired
   * @param key Cache key
   * @param maxAge Maximum age of cache in milliseconds (default: 1 hour)
   */
  get(key: string, maxAge: number = 60 * 60 * 1000): any | null {
    if (!this.cache.has(key)) {
      return null;
    }
    
    const entry = this.cache.get(key);
    if (!entry) {
      return null;
    }
    
    const now = Date.now();
    
    if (now - entry.timestamp > maxAge) {
      // Cache expired
      this.cache.delete(key);
      return null;
    }
    
    return entry.data;
  }
  
  /**
   * Set data in cache
   * @param key Cache key
   * @param data Data to cache
   */
  set(key: string, data: any): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }
  
  /**
   * Clear entire cache or specific key
   * @param key Optional key to clear specific entry
   */
  clear(key?: string): void {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }
  
  /**
   * Wrap an observable with caching logic
   * @param key Cache key
   * @param observable Observable to cache
   * @param maxAge Maximum age of cache in milliseconds (default: 1 hour)
   * @param forceRefresh Force refresh regardless of cache
   */
  cacheRequest<T>(
    key: string, 
    observable: Observable<T>, 
    maxAge: number = 60 * 60 * 1000,
    forceRefresh: boolean = false
  ): Observable<T> {
    if (!forceRefresh) {
      const cachedData = this.get(key, maxAge);
      if (cachedData) {
        return of(cachedData);
      }
    }
    
    return observable.pipe(
      tap(data => this.set(key, data))
    );
  }
} 